import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Checkbox, NumberInput, Radio } from '@mantine/core';
import AppStack from '../../common/AppStack';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import MediaDropzone from '../media/MediaDropzone';
import AppCheckbox from '../../common/AppCheckbox';
import PaginationList from '../../common/PaginationList';
import { singularPluralFormat } from '../../../helpers/format';

const intialState = {
  templateType: 'images',
  showProductSelect: false,
  selectedProductKeys: [],
  images: [],
  selectedImageKeys: [],
  blankTemplateCount: 1,
  pageIndex: 1
};

const PrintDesignTemplateCreateModal = ({
  fkEcomVendor,
  fkEcomStore,
  ecomExternalProducts,
  isOpen,
  onClose,
  onConfirm
}) => {
  const [formState, setFormState] = useState({ ...intialState });
  const allProductsSelected = ecomExternalProducts?.every((p) =>
    formState.selectedProductKeys.includes(p.id)
  );

  useEffect(() => {
    if (isOpen) {
      setFormState({
        ...intialState,
        selectedProductKeys: ecomExternalProducts?.map((p) => p.id) || []
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={
        formState.showProductSelect
          ? {
              onCancel: () => {
                setFormState({
                  ...formState,
                  showProductSelect: false
                });
              },
              onSubmit: () => {
                setFormState({
                  ...formState,
                  loading: true
                });
                onConfirm(
                  {
                    images: formState.images,
                    productIds: formState.selectedProductKeys,
                    templateType: formState.templateType,
                    blankTemplateCount: formState.blankTemplateCount
                  },
                  () => {
                    setFormState({
                      ...formState,
                      loading: false
                    });
                  }
                );
              },
              submitColor: 'dark',
              submitTitle: `Yes, add ${
                formState.images.length === 1 &&
                formState.selectedProductKeys.length === 1
                  ? 'desgin'
                  : 'designs'
              }`,
              isSubmitDisabled: formState.selectedProductKeys.length === 0,
              cancelTitle: 'Back',
              isLoading: formState.loading
            }
          : {
              onCancel: onClose,
              onSubmit: () => {
                setFormState({
                  ...formState,
                  showProductSelect: true
                });
              },
              submitColor: 'dark',
              submitTitle: 'Continue',
              isSubmitDisabled:
                formState.templateType === 'images'
                  ? formState.images.length === 0
                  : formState.blankTemplateCount < 1,
              cancelTitle: 'Cancel',
              isLoading: formState.loading
            }
      }
      onClose={onClose}
      opened={isOpen}
      size={600}
      title="Add new print design"
    >
      {formState.showProductSelect ? (
        <AppStack style={{ gap: 16, paddingBottom: 10 }}>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            Select products to create designs for:
          </AppText>
          <AppStack style={{ gap: 12 }}>
            <Checkbox
              checked={allProductsSelected}
              disabled={formState.loading}
              label={allProductsSelected ? 'Unselect all' : 'Select all'}
              onChange={() => {
                setFormState({
                  ...formState,
                  selectedProductKeys: allProductsSelected
                    ? []
                    : ecomExternalProducts.map((p) => p.id)
                });
              }}
            />

            <PaginationList
              emptyMessage="No products found."
              items={ecomExternalProducts
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((p) => {
                  const selected = formState.selectedProductKeys.some(
                    (k) => k === p.id
                  );

                  return (
                    <AppFlexbox
                      key={p.id}
                      onClick={() => {
                        setFormState({
                          ...formState,
                          selectedProductKeys: selected
                            ? [
                                ...formState.selectedProductKeys.filter(
                                  (k) => k !== p.id
                                )
                              ]
                            : [...formState.selectedProductKeys, p.id]
                        });
                      }}
                      style={{
                        gap: 8,
                        padding: 8,
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        disabled={formState.loading}
                        onChange={() => {}}
                      />
                      <AppFlexbox
                        style={{
                          flex: 1,
                          gap: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppStack style={{ gap: 0 }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 500
                            }}
                          >
                            {p.name}
                          </AppText>

                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666'
                            }}
                          >
                            {singularPluralFormat(
                              p.ecomVendorProductVariants.length || 0,
                              'variant',
                              'variants'
                            )}
                          </AppText>
                        </AppStack>
                      </AppFlexbox>
                    </AppFlexbox>
                  );
                })}
              itemsPerPage={10}
              onPageChange={(pageIndex) =>
                setFormState({
                  ...formState,
                  pageIndex
                })
              }
              pageIndex={formState.pageIndex}
            />
          </AppStack>
        </AppStack>
      ) : (
        <AppStack style={{ gap: 16, paddingBottom: 10 }}>
          <Radio.Group
            label="Select how to create the design"
            onChange={(value) => {
              setFormState((prevState) => ({
                ...prevState,
                templateType: value
              }));
            }}
            value={formState.templateType}
          >
            <AppStack style={{ gap: 8, marginTop: 8 }}>
              <Radio label="Create from images" value="images" />
              <Radio label="Create blank" value="blank" />
            </AppStack>
          </Radio.Group>

          {formState.templateType === 'images' ? (
            <AppStack style={{ gap: 8 }}>
              {!formState.selectedImageKeys.length ? (
                <AppFlexbox
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 16
                  }}
                >
                  <AppFlexbox
                    style={{ alignItems: 'center', gap: 8, cursor: 'pointer' }}
                  >
                    <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                      Images
                    </AppText>
                  </AppFlexbox>
                </AppFlexbox>
              ) : (
                <AppFlexbox
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 16
                  }}
                >
                  <AppFlexbox
                    onClick={() =>
                      setFormState({ ...formState, selectedImageKeys: [] })
                    }
                    style={{ alignItems: 'center', gap: 8, cursor: 'pointer' }}
                  >
                    <AppCheckbox checked onChange={() => {}} size="xs" />
                    <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                      {formState.selectedImageKeys.length} selected
                    </AppText>
                  </AppFlexbox>

                  <Anchor
                    onClick={() => {
                      if (!formState.loading) {
                        setFormState({
                          ...formState,
                          images: formState.images.filter(
                            (i) => !formState.selectedImageKeys.includes(i.key)
                          ),
                          selectedImageKeys: []
                        });
                      }
                    }}
                    style={{ fontSize: 14, color: '#c40000', fontWeight: 500 }}
                  >
                    Remove
                  </Anchor>
                </AppFlexbox>
              )}
              <MediaDropzone
                disabled={formState.loading}
                enableMediaSelect
                files={formState.images}
                fkEcomStore={fkEcomStore}
                fkEcomVendor={fkEcomVendor}
                onSelectFile={(file) => {
                  if (
                    !formState.selectedImageKeys.find((k) => k === file.key)
                  ) {
                    setFormState({
                      ...formState,
                      selectedImageKeys: [
                        ...formState.selectedImageKeys,
                        file.key
                      ]
                    });
                  }
                  else {
                    setFormState({
                      ...formState,
                      selectedImageKeys: [
                        ...formState.selectedImageKeys.filter(
                          (k) => k !== file.key
                        )
                      ]
                    });
                  }
                }}
                onUpload={(uploadedMedia) => {
                  const images = [
                    ...formState.images,
                    ...uploadedMedia
                      .filter(
                        (m) => !formState.images.some((f) => f.key === m.key)
                      )
                      .map((m, index) => ({
                        key: (new Date().getTime() + index).toString(),
                        ...m,
                        file: m.file,
                        src: m.src,
                        preview: m.preview,
                        contentType: m.contentType,
                        alt: m.alt,
                        designName: m.alt,
                        deleted: false
                      }))
                  ].map((m, i) => ({
                    ...m,
                    sort: i + 1
                  }));

                  setFormState({
                    ...formState,
                    images
                  });
                }}
                selectedMediaKeys={formState.selectedImageKeys}
                uploadOnDrop
              />
            </AppStack>
          ) : (
            <AppStack style={{ gap: 8 }}>
              <NumberInput
                allowDecimal={false}
                label="Number of designs"
                min={1}
                onChange={(value) => {
                  setFormState({
                    ...formState,
                    blankTemplateCount: value || 1
                  });
                }}
                required
                value={formState.blankTemplateCount}
              />
            </AppStack>
          )}
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

PrintDesignTemplateCreateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  ecomExternalProducts: PropTypes.array,
  fkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fkEcomVendor: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default PrintDesignTemplateCreateModal;
